import React, { useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'wouter';
import { toast } from 'react-hot-toast';
import { post } from '../../lib/api';
import { secretAtom, writeSecretAtom, writeSessionAtom } from '../../atoms/auth';

export function LoginPage() {
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);
  const secret     = useAtomValue(secretAtom);
  const [, setLocation] = useLocation();
  const setSecret  = useSetAtom(writeSecretAtom);
  const setSession = useSetAtom(writeSessionAtom);

  useEffect(() => {
    if (secret) {
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('r');
      if (redirectUrl && redirectUrl.startsWith('/')) {
        setLocation(redirectUrl);
      } else {
        setLocation('/');
      }
    }
  }, [secret, setLocation]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    const id = toast.loading('Authenticating...');
    try {
      sessionStorage.setItem('op_secret', password.trim());
      const data = await post<{ session_token: string }>('/panel/data');
      setSecret(password.trim());
      setSession(data.session_token);
      toast.success('Access granted', { id });
    } catch (err: unknown) {
      sessionStorage.removeItem('op_secret');
      toast.error(err instanceof Error ? err.message : 'Invalid password', { id });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090f] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Cipher backdrop */}
      <div className="cipher-backdrop absolute inset-0 font-mono text-[9px] text-cyberGreen/[0.04] select-none pointer-events-none z-0 p-8 leading-none tracking-widest break-all">
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={i}>SC_LABS_COMMAND_DECK_SECURE_ACCESS_2026_PBKDF2_HMAC_SHA256_OWNER_PANEL_</div>
        ))}
      </div>

      {/* Green glow blobs */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(0,255,102,0.04)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full blur-[80px] pointer-events-none" style={{ background: 'rgba(0,255,102,0.03)' }} />

      {/* Glass login card */}
      <div className="glass-login-card relative z-10 w-full max-w-md rounded-3xl p-8 flex flex-col gap-6 overflow-hidden">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyberGreen to-transparent" />

        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border border-cyberGreen/10 animate-pulse" />
            <div className="absolute inset-2 rounded-full border border-dashed border-cyberGreen/20 animate-spin" style={{ animationDuration: '10s' }} />
            <div className="absolute inset-4 rounded-full border border-cyberGreen/40 animate-ping" style={{ animationDuration: '2.5s' }} />
            <div className="relative w-14 h-14 rounded-2xl bg-cyberGreen/10 border border-cyberGreen/20 flex items-center justify-center shadow-[0_0_24px_rgba(0,255,102,0.15)]">
              <i className="fa-solid fa-shield-halved text-cyberGreen text-2xl neon-text-glow" />
            </div>
          </div>
          <div className="text-center">
            <h2 className="font-black text-2xl text-white tracking-tight uppercase">
              SC <span className="text-cyberGreen neon-text-glow">LABS</span>
            </h2>
            <div className="text-[9px] font-mono text-slate-500 tracking-[0.2em] mt-0.5">COMMAND DECK · OWNER ACCESS</div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-extrabold tracking-[0.15em] uppercase text-slate-400">
              Panel Password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={loading}
                autoFocus
                className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 pr-12 text-slate-100 placeholder:text-slate-800 text-sm font-mono focus:outline-none focus:border-cyberGreen focus:ring-1 focus:ring-cyberGreen/50 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3.5 top-3.5 text-slate-600 hover:text-slate-400 transition-colors"
              >
                <i className={`fa-solid ${showPw ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-1 py-3 bg-cyberGreen hover:bg-cyberGreen/90 text-slate-950 font-black text-sm rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-cyberGreen/20 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading
              ? <><i className="fa-solid fa-spinner animate-spin" /> Authenticating...</>
              : <><i className="fa-solid fa-right-to-bracket" /> Access Panel</>
            }
          </button>
        </form>

        <div className="flex items-center justify-center gap-2 text-[9px] font-mono text-slate-700">
          <i className="fa-solid fa-lock text-cyberGreen/30" />
          <span>PBKDF2-HMAC-SHA256</span>
          <span className="text-slate-800">·</span>
          <span>Restricted access — authorised personnel only</span>
        </div>
      </div>
    </div>
  );
}
